import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import InputBase from '@mui/material/InputBase';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PeopleOutlineRoundedIcon from '@mui/icons-material/PeopleOutlineRounded';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WebhookRoundedIcon from '@mui/icons-material/WebhookRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import GroupWorkRoundedIcon from '@mui/icons-material/GroupWorkRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import DynamicFormRoundedIcon from '@mui/icons-material/DynamicFormRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { colors } from '../../theme';
import { usePermissions } from '../../hooks/usePermissions';
import { useSocket } from '../../hooks/useSocket';
import * as conversationService from '../../services/conversationService';

export const DRAWER_WIDTH = 232;

// Every item's visibility is a permission check - agents simply never see
// links to pages they can't open (PermissionRoute is the hard backstop).
const MAIN_ITEMS = [
  { label: 'Dashboard', path: '/', icon: <HomeOutlinedIcon />, module: 'dashboard', action: 'view', end: true },
  { label: 'Inbox', path: '/inbox', icon: <MessageOutlinedIcon />, module: 'inbox', action: 'view', badge: 'unread' },
  { label: 'Bulk Messages', path: '/bulk-messaging', icon: <SendOutlinedIcon />, module: 'bulk_send', action: 'view' },
  { label: 'Templates', path: '/templates', icon: <DescriptionOutlinedIcon />, module: 'templates', action: 'view' },
  { label: 'Contacts', path: '/contacts', icon: <PeopleOutlineRoundedIcon />, module: 'contacts', action: 'view' },
  { label: 'Campaigns', path: '/campaigns', icon: <CampaignOutlinedIcon />, module: 'bulk_send', action: 'view' },
  { label: 'Analytics', path: '/analytics', icon: <BarChartOutlinedIcon />, module: 'analytics', action: 'view' },
  { label: 'Numbers', path: '/settings', icon: <SmartphoneOutlinedIcon />, module: 'whatsapp_settings', action: 'view' },
  { label: 'Settings', path: '/company-profile', icon: <SettingsOutlinedIcon />, module: 'company', action: 'view' },
];

// Everything else stays reachable under "More".
// TEMPORARILY HIDDEN: all "More" groups (Contact tools, Messaging, Automation & AI,
// CRM, Support, Organization). Restore by uncommenting the array below.
const NAV_GROUPS = [];
/*
const NAV_GROUPS_DISABLED = [
  {
    label: 'Contact tools',
    icon: <SellRoundedIcon />,
    items: [
      { label: 'Tags', path: '/tags', icon: <SellRoundedIcon />, module: 'contacts', action: 'manage' },
      { label: 'Labels', path: '/labels', icon: <LabelRoundedIcon />, module: 'contacts', action: 'manage' },
      { label: 'Groups', path: '/groups', icon: <GroupWorkRoundedIcon />, module: 'contacts', action: 'manage' },
      { label: 'Segments', path: '/segments', icon: <FilterAltRoundedIcon />, module: 'contacts', action: 'manage' },
      { label: 'Custom Fields', path: '/custom-fields', icon: <DynamicFormRoundedIcon />, module: 'contacts', action: 'manage' },
    ],
  },
  {
    label: 'Messaging',
    icon: <WhatsAppIcon />,
    items: [
      { label: 'WhatsApp Inbox', path: '/whatsapp-inbox', icon: <WhatsAppIcon />, module: 'message_history', action: 'view' },
      { label: 'Message History', path: '/history', icon: <HistoryRoundedIcon />, module: 'message_history', action: 'view' },
      { label: 'Webhook Logs', path: '/webhook-logs', icon: <WebhookRoundedIcon />, module: 'whatsapp_settings', action: 'view' },
    ],
  },
  {
    label: 'Automation & AI',
    icon: <BoltRoundedIcon />,
    items: [
      { label: 'Automation', path: '/automation', icon: <BoltRoundedIcon />, module: 'automation', action: 'view' },
      { label: 'AI Chatbot', path: '/chatbots', icon: <SmartToyRoundedIcon />, module: 'chatbot', action: 'view' },
    ],
  },
  {
    label: 'CRM',
    icon: <HandshakeRoundedIcon />,
    items: [
      { label: 'Leads', path: '/leads', icon: <PersonSearchRoundedIcon />, module: 'crm_leads', action: 'view' },
      { label: 'Deals', path: '/deals', icon: <HandshakeRoundedIcon />, module: 'crm_deals', action: 'view' },
      { label: 'Sales Dashboard', path: '/sales-dashboard', icon: <InsightsRoundedIcon />, module: 'crm_deals', action: 'view' },
    ],
  },
  {
    label: 'Support',
    icon: <ConfirmationNumberRoundedIcon />,
    items: [{ label: 'Support Tickets', path: '/tickets', icon: <ConfirmationNumberRoundedIcon />, module: 'tickets', action: 'view' }],
  },
  {
    label: 'Organization',
    icon: <BusinessRoundedIcon />,
    items: [
      { label: 'Branding', path: '/branding', icon: <PaletteRoundedIcon />, module: 'company', action: 'manage' },
      { label: 'Users', path: '/users', icon: <PeopleAltRoundedIcon />, module: 'team_members', action: 'view' },
      { label: 'Roles & Permissions', path: '/roles', icon: <AdminPanelSettingsRoundedIcon />, module: 'roles', action: 'view' },
      { label: 'Departments', path: '/departments', icon: <AccountTreeRoundedIcon />, module: 'departments', action: 'view' },
      { label: 'Designations', path: '/designations', icon: <BadgeRoundedIcon />, module: 'designations', action: 'view' },
      { label: 'Teams', path: '/teams', icon: <GroupsRoundedIcon />, module: 'teams', action: 'view' },
      { label: 'API Keys', path: '/api-keys', icon: <VpnKeyRoundedIcon />, module: 'company', action: 'manage' },
      { label: 'Webhooks & Events', path: '/webhooks', icon: <WebhookRoundedIcon />, module: 'webhooks', action: 'view' },
      { label: 'Notification Settings', path: '/notification-settings', icon: <NotificationsRoundedIcon /> },
      { label: 'Security', path: '/security', icon: <ShieldRoundedIcon /> },
      { label: 'Support', path: '/support', icon: <SupportAgentRoundedIcon /> },
      { label: 'Activity Log', path: '/activity-logs', icon: <ReceiptLongRoundedIcon />, module: 'activity_logs', action: 'view' },
      { label: 'Subscription', path: '/subscription', icon: <WorkspacePremiumRoundedIcon />, module: 'subscription', action: 'view' },
      { label: 'Wallet', path: '/wallet', icon: <AccountBalanceWalletRoundedIcon />, module: 'subscription', action: 'view' },
      { label: 'Billing', path: '/billing', icon: <CreditCardRoundedIcon />, module: 'subscription', action: 'view' },
    ],
  },
];
*/

const tileSx = {
  color: colors.aa.bodyText,
  borderRadius: '12px',
  minHeight: 40,
  py: 0.5,
  px: 1.5,
  bgcolor: 'rgba(255,255,255,0.75)',
  fontWeight: 500,
  '& .MuiListItemText-primary': { fontSize: 13, fontWeight: 500 },
  '& .MuiListItemIcon-root': { color: colors.aa.bodyText, minWidth: 32, '& svg': { fontSize: 19 } },
  '&:hover': { bgcolor: '#fff' },
  '&.active': {
    bgcolor: colors.emerald[600],
    color: '#fff',
    '& .MuiListItemText-primary': { fontWeight: 600 },
    '& .MuiListItemIcon-root': { color: '#fff' },
    '&:hover': { bgcolor: colors.emerald[600] },
  },
};

const childSx = {
  ...tileSx,
  minHeight: 34,
  bgcolor: 'transparent',
  px: 1.25,
  '& .MuiListItemText-primary': { fontSize: 12.5, fontWeight: 500 },
  '& .MuiListItemIcon-root': { color: colors.aa.mutedText, minWidth: 28, '& svg': { fontSize: 16 } },
  '&:hover': { bgcolor: 'rgba(255,255,255,0.8)' },
};

function Badge({ value }) {
  if (!value) return null;
  return (
    <Box
      sx={{
        minWidth: 22,
        height: 22,
        px: 0.75,
        borderRadius: 11,
        bgcolor: '#E5484D',
        color: '#fff',
        fontSize: 11.5,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {value > 99 ? '99+' : value}
    </Box>
  );
}

function NavTile({ item, unread, sx = tileSx }) {
  return (
    <ListItemButton component={NavLink} to={item.path} end={item.end} sx={sx}>
      <ListItemIcon>{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} />
      {item.badge === 'unread' && <Badge value={unread} />}
    </ListItemButton>
  );
}

function MoreGroup({ group, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <ListItemButton onClick={() => setOpen((v) => !v)} sx={{ ...childSx, fontWeight: 600 }}>
        <ListItemIcon>{group.icon}</ListItemIcon>
        <ListItemText primary={group.label} primaryTypographyProps={{ fontSize: 12.5, fontWeight: 600 }} />
        <ExpandMoreRoundedIcon
          sx={{ fontSize: 18, color: colors.aa.mutedText, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
        />
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List sx={{ ml: 2, pl: 0.5, py: 0, borderLeft: `1px solid ${colors.aa.borderStrong}`, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {group.items.map((item) => (
            <NavTile key={item.path} item={item} sx={childSx} />
          ))}
        </List>
      </Collapse>
    </>
  );
}

export default function Sidebar() {
  const { can } = usePermissions();
  const { socket } = useSocket();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [unread, setUnread] = useState(0);
  const searchRef = useRef(null);

  // Inbox badge: total unread conversations. Refreshed on socket activity and
  // when the tab regains focus so it never drifts far from the inbox itself.
  useEffect(() => {
    let alive = true;
    const load = () =>
      conversationService
        .getCounts()
        .then((res) => alive && setUnread(Number(res.data?.unread_count || 0)))
        .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    window.addEventListener('focus', load);
    socket?.on?.('conversation:message', load);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener('focus', load);
      socket?.off?.('conversation:message', load);
    };
  }, [socket, location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const allowed = (item) => !item.module || can(item.module, item.action);
  const mainItems = useMemo(() => MAIN_ITEMS.filter(allowed), [can]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(
    () => NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(allowed) })).filter((g) => g.items.length > 0),
    [can] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const q = query.trim().toLowerCase();
  const searchResults = q
    ? [...mainItems, ...groups.flatMap((g) => g.items)].filter((i) => i.label.toLowerCase().includes(q))
    : null;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: '#F6F9FB',
          color: colors.aa.bodyText,
          borderRight: 'none',
          px: 1.25,
        },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 0.75, pt: 2, pb: 1.75 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '11px',
            bgcolor: colors.emerald[600],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MessageOutlinedIcon sx={{ color: '#fff', fontSize: 21 }} />
        </Box>
        <Typography sx={{ color: colors.aa.headText, fontWeight: 800, fontSize: 17, letterSpacing: '-0.02em' }}>
          ClientConnect
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mx: 0.25, mb: 1.5, px: 1.5, height: 40, borderRadius: '12px', bgcolor: '#EDF1F5' }}
      >
        <SearchRoundedIcon sx={{ fontSize: 17, color: colors.aa.mutedText }} />
        <InputBase
          inputRef={searchRef}
          fullWidth
          placeholder="Search (Ctrl+K)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && setQuery('')}
          sx={{ fontSize: 13, '& input::placeholder': { color: colors.aa.mutedText, opacity: 1 } }}
        />
      </Stack>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          pb: 2,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {searchResults ? (
          <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 0.25 }}>
            {searchResults.map((item) => (
              <NavTile key={item.path} item={item} unread={unread} />
            ))}
            {searchResults.length === 0 && (
              <Typography sx={{ px: 1.5, py: 1, fontSize: 12.5, color: colors.aa.mutedText }}>No matches</Typography>
            )}
          </List>
        ) : (
          <>
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 0.25 }}>
              {mainItems.map((item) => (
                <NavTile key={item.path} item={item} unread={unread} />
              ))}
            </List>

            {groups.length > 0 && (
              <>
                <Typography
                  sx={{ px: 1.75, pt: 2.25, pb: 0.75, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: colors.aa.mutedText }}
                >
                  MORE
                </Typography>
                <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, p: 0.25 }}>
                  {groups.map((group) => (
                    <MoreGroup
                      key={group.label}
                      group={group}
                      defaultOpen={group.items.some((item) => location.pathname.startsWith(item.path))}
                    />
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}
